import React, { useEffect } from 'react';
import { useParams, useLocation, Navigate, Outlet } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import NoIndex from '@/components/seo/NoIndex';
import NotFound from '@/pages/NotFound';

/**
 * Reads the :locale param from the URL (e.g. /de/…) and syncs it into the
 * LanguageContext on mount / change.
 *
 * Hinweis: Die öffentliche englische Version ist derzeit deaktiviert
 * (nur DACH). Alle /en/* URLs werden auf die entsprechende /de/* URL
 * umgeleitet. Die Übersetzungsdateien bleiben für eine spätere
 * Expansion erhalten.
 *
 * SEO: Die Weiterleitung ist technisch KEIN serverseitiger 301, weil das
 * Hosting alle SPA-Pfade mit HTTP 200 ausliefert. Der EN-Zwischenzustand
 * wird deshalb zusätzlich mit noindex,nofollow markiert.
 *
 * Unbekannte Locale-Segmente (z. B. /irgendwas) dürfen nicht als
 * Startseite gerendert werden – sonst entsteht ein Soft-404.
 */
const KNOWN_LOCALES = ['de', 'en'];

export const LocaleRouter: React.FC = () => {
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  const isEnglish = locale === 'en';
  const isKnownLocale = !!locale && KNOWN_LOCALES.includes(locale);

  useEffect(() => {
    if (!isKnownLocale) return;
    if (isEnglish) {
      // EN öffentlich deaktiviert – gespeicherte Sprache auf Deutsch zurücksetzen
      if (language !== 'de') setLanguage('de');
      return;
    }
    if (locale === 'de' && language !== 'de') {
      setLanguage('de');
    }
  }, [locale, language, setLanguage, isEnglish, isKnownLocale]);

  if (!isKnownLocale) {
    return (
      <>
        <NoIndex />
        <NotFound />
      </>
    );
  }

  if (isEnglish) {
    const target = location.pathname.replace(/^\/en(?=\/|$)/, '/de') || '/de';
    return (
      <>
        <NoIndex />
        <Navigate to={target + location.search + location.hash} replace />
      </>
    );
  }

  return <Outlet />;
};
